/**
 * Splits a block a rep pasted into candidate rows they then confirm one at a time.
 *
 * Deterministic and dull on purpose. The design's objection to the competitor's single free-text
 * FAQ field is that a parsed paragraph has no boundary and no scope, so this exists to give the rep
 * boundaries to correct rather than to be clever: what it cannot split confidently it hands back as
 * `unparsed` for the rep to place, instead of inventing a question for it.
 *
 * Nothing here writes. A row becomes a fact only when the rep saves it through the entries route,
 * where its key's own contract and the personal-data refusal apply, as they do for every writer.
 */

/** The database allows 500 characters of question; a longer one is the rep's to shorten. */
export const FAQ_QUESTION_LIMIT = 500;
/** One paste is a page of FAQ, not an import. More than this is a sign the block is prose. */
export const FAQ_PASTE_ROW_LIMIT = 50;

interface PastedFaqRow {
  readonly question: string;
  readonly answer: string;
  /** True when the question ran past what the field holds, so the rep is asked to shorten it. */
  readonly questionTooLong: boolean;
}

export interface PastedFaq {
  readonly rows: readonly PastedFaqRow[];
  /** Blocks with no question in them, handed back verbatim rather than guessed at. */
  readonly unparsed: readonly string[];
  /** Rows beyond the limit, dropped and counted so the rep is not told everything arrived. */
  readonly dropped: number;
}

const LABELLED_QUESTION = /^\s*(?:q|question)\s*[:.)\-–]\s*/iu;
const LABELLED_ANSWER = /^\s*(?:a|answer)\s*[:.)\-–]\s*/iu;

function clean(lines: readonly string[]): string {
  return lines
    .join("\n")
    .replaceAll(/[ \t]+$/gmu, "")
    .trim();
}

function row(question: string, answer: string): PastedFaqRow | null {
  const asked = question.trim();
  const said = answer.trim();
  if (asked.length === 0 || said.length === 0) return null;
  return { question: asked, answer: said, questionTooLong: asked.length > FAQ_QUESTION_LIMIT };
}

/**
 * One block, by the three shapes people actually paste.
 *
 * Labelled `Q:`/`A:` first, because it is unambiguous. Then a first line that is itself a question.
 * Then a question mark inside the first line, which is how a sentence and its answer arrive on one
 * line. Anything else has no question in it and is not a FAQ row.
 */
function parseBlock(block: string): PastedFaqRow | null {
  const lines = block.split("\n");
  const answerAt = lines.findIndex((line) => LABELLED_ANSWER.test(line));
  const first = lines[0] ?? "";
  if (LABELLED_QUESTION.test(first) && answerAt > 0)
    return row(
      clean([first.replace(LABELLED_QUESTION, ""), ...lines.slice(1, answerAt)]),
      clean([(lines[answerAt] ?? "").replace(LABELLED_ANSWER, ""), ...lines.slice(answerAt + 1)]),
    );
  if (first.trim().endsWith("?") && lines.length > 1)
    return row(clean([first]), clean(lines.slice(1)));
  const mark = first.indexOf("?");
  if (mark >= 0 && mark < first.length - 1)
    return row(first.slice(0, mark + 1), clean([first.slice(mark + 1), ...lines.slice(1)]));
  return null;
}

export function parsePastedFaq(text: string): PastedFaq {
  const blocks = text
    .replaceAll("\r\n", "\n")
    .split(/\n\s*\n/u)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
  const rows: PastedFaqRow[] = [];
  const unparsed: string[] = [];
  let dropped = 0;
  for (const block of blocks) {
    const parsed = parseBlock(block);
    if (!parsed) {
      unparsed.push(block);
      continue;
    }
    if (rows.length >= FAQ_PASTE_ROW_LIMIT) {
      dropped += 1;
      continue;
    }
    rows.push(parsed);
  }
  return { rows, unparsed, dropped };
}
